import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { createPaymentCoupon } from '../../../../../../../../crud/payments_api/payment_coupons';
import { Coupon } from '../../../../types';
import { toast } from 'react-toastify';
import { X } from 'react-bootstrap-icons';
import { FaInfoCircle } from 'react-icons/fa';
// Calendar now handled inside CouponLimits component
import './CreateCoupon.scss';
import CouponType from './components/CouponType/CouponType';
import CouponLimits from './components/CouponLimits/CouponLimits';
import CouponReach from './components/CouponReach/CouponReach';

interface RenderCreateCouponsModalProps {
  itemOptions: any[];
  menuOptions: any[];
  categoryOptions: any[];
  show: boolean;
  handleClose: () => void;
  setCoupon: any;
  coupons: Coupon[];
  setShowCreateCouponModal: any;
}


const CreateCoupon: React.FC<RenderCreateCouponsModalProps> = ({
  show,
  handleClose,
  setCoupon,
  coupons,
  itemOptions,
  menuOptions,
  categoryOptions,
  setShowCreateCouponModal
}) => {
  const [couponName, setCouponName] = useState('');
  const [couponType, setCouponType] = useState('');
  const [limitType, setLimitType] = useState('Sin limite');
  const [applyOn, setApplyOn] = useState('Global');

  const [isValid, setIsValid] = useState(true);
  const [maxNumber, setMaxNumber] = useState<number | null>(null);
  const [maxBudget, setMaxBudget] = useState<number | null>(null);
  const [isGlobal, setIsGlobal] = useState(true);
  const [categoryId, setCategoryId] = useState('');
  const [menuId, setMenuId] = useState('');
  const [itemId, setItemId] = useState('');
  const [localId, setLocalId] = useState<number | null>(null);
  const [couponAmount, setCouponAmount] = useState<number | null>(null);
  const [couponPercentage, setCouponPercentage] = useState<number | null>(null);
  const [amountError, setAmountError] = useState<string>('');

  // Estados para el calendario interactivo
  const [selectedExpirationDate, setSelectedExpirationDate] = useState<Date | null>(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showInfoBanner, setShowInfoBanner] = useState(false);
  const [showAlcanceExplanation, setShowAlcanceExplanation] = useState('');

  // Opciones seleccionadas (si existen)
  const selectedCategory = categoryOptions.find((cat) => cat.id === categoryId);
  const selectedItem = itemOptions.find((itm) => itm.id === itemId);

  useEffect(() => {
    const userContext = localStorage.getItem('user');
    const localIdFromStorage = userContext ? JSON.parse(userContext).local_id : 0;
    setLocalId(localIdFromStorage);
  }, []);

  useEffect(() => {
    // Resetear campos al cerrar el modal de creación
    if (!show) {
      setCouponName('');
      setSelectedExpirationDate(null);
      setCouponType('');
      setLimitType('Sin limite');
      setApplyOn('Global');
      setIsValid(true);
      setMaxNumber(null);
      setMaxBudget(null);
      setIsGlobal(true);
      setCategoryId('');
      setMenuId('');
      setItemId('');
      setCouponAmount(null);
      setCouponPercentage(null);
      setShowCalendar(false);
      setShowInfoBanner(false);
      setAmountError('');
    }
  }, [show]);

  const handleCloseModal = () => {
    setShowCreateCouponModal(false);
  };

  const handleCreateCoupon = async () => {
    try {
      // Convertir la fecha seleccionada a formato "YYYY-MM-DD"
      const formattedExpiration = selectedExpirationDate
        ? selectedExpirationDate.toISOString().split('T')[0]
        : '';
      const payload = {
        payment_coupon_name: couponName,
        expiration_date: formattedExpiration === '' ? null : formattedExpiration,
        isvalid: isValid,
        max_number: maxNumber,
        max_budget: maxBudget,
        isglobal: isGlobal,
        menu_category_id: categoryId === '' ? null : categoryId,
        menu_id: menuId === '' ? null : menuId,
        item_id: itemId === '' ? null : itemId,
        local_id: localId,
        coupon_amount: couponAmount,
        coupon_percentage: couponPercentage,
      };
      console.log('newCoupon:', payload);

      const response = await createPaymentCoupon(payload);
      const newCoupon = (response as any).data;
      setCoupon([...coupons, newCoupon]);
      toast.success('Cupón creado exitosamente');
      handleClose();
    } catch (error: any) {
      if (error?.response?.data?.message === 'The coupon amount is greater than the price of the item') {
        setAmountError('El monto del descuento no puede ser mayor o igual al valor del ítem seleccionado.');
      } else {
        console.error('Error al crear el cupón:', error);
        toast.error('Error al crear el cupón');
      }
    }
  };

  // Selection handlers moved into CouponReach component

  // Validación del monto
  useEffect(() => {
    if (couponType === 'Amount' && couponAmount !== null && selectedItem && selectedItem.price !== undefined) {
      if (couponAmount > selectedItem.price) {
        setAmountError('El monto del descuento no puede ser mayor que el valor del ítem seleccionado.');
      } else {
        setAmountError('');
      }
    } else {
      setAmountError('');
    }
  }, [couponAmount, selectedItem, couponType]);

  return (
    <Modal show={show} onHide={handleCloseModal} className="modal-cupones" size="lg">
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">Crear cupón</Modal.Title>
        </div>
        <Button variant="none" className="modal-close-button" onClick={handleCloseModal}>
          <X size={24} />
        </Button>
      </Modal.Header>
      
      <Modal.Body>
        {/* Banner informativo */}
        <div className="coupon-info-banner">
          <div className="banner-header" onClick={() => setShowInfoBanner(!showInfoBanner)}>
            <div className="header-content">
              <div className="header-icon">
                <FaInfoCircle />
              </div>
              <div className="header-title">¿Qué es un cupón?</div>
            </div>
            <div className={`toggle-icon ${showInfoBanner ? 'expanded' : 'collapsed'}`}>
              {showInfoBanner ? '−' : '+'}
            </div>
          </div>
          {showInfoBanner && (
            <div className="banner-content">
              Una plantilla sobre la cual se pueden generar cupones individuales que pueden ser compartidos con clientes. 
              Por ejemplo, si creas un cupón <strong>"VIP $1.000"</strong>, entonces luego podrás generar códigos QR para tus clientes usando esta plantilla.
            </div>
          )}
        </div>

        <Form className="coupon-form" onSubmit={(e) => { e.preventDefault(); handleCreateCoupon(); }}>
          {/* Nombre del cupón */}
          <div className="form-section name-section">
            <Form.Group className="mp-formgroup">
              <Form.Control
                type="text"
                value={couponName}
                onChange={(e) => setCouponName(e.target.value)}
                required
                placeholder="Nombre del cupón (Ej: Descuento VIP, Promo Fin de Semana)"
                className="coupon-name-input"
              />
            </Form.Group>
          </div>

          {/* Tipo de cupón modularizado */}
          <CouponType
            couponType={couponType}
            setCouponType={setCouponType}
            couponAmount={couponAmount}
            setCouponAmount={setCouponAmount}
            couponPercentage={couponPercentage}
            setCouponPercentage={setCouponPercentage}
            amountError={amountError}
          />

          <CouponLimits
            limitType={limitType}
            setLimitType={setLimitType}
            selectedExpirationDate={selectedExpirationDate}
            setSelectedExpirationDate={setSelectedExpirationDate}
            showCalendar={showCalendar}
            setShowCalendar={setShowCalendar}
            maxBudget={maxBudget}
            setMaxBudget={setMaxBudget}
            maxNumber={maxNumber}
            setMaxNumber={setMaxNumber}
          />

          <CouponReach
            applyOn={applyOn}
            setApplyOn={setApplyOn}
            menuOptions={menuOptions}
            categoryOptions={categoryOptions}
            itemOptions={itemOptions}
            menuId={menuId}
            setMenuId={setMenuId}
            categoryId={categoryId}
            setCategoryId={setCategoryId}
            itemId={itemId}
            setItemId={setItemId}
            setIsGlobal={setIsGlobal}
            showAlcanceExplanation={showAlcanceExplanation}
            setShowAlcanceExplanation={setShowAlcanceExplanation}
            selectedCategory={selectedCategory}
            selectedItem={selectedItem}
          />
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button className="form-button-cancel" variant="none" onClick={handleClose}>
            Cerrar
          </Button>
          <Button
            className="form-button-create"
            variant="none"
            onClick={() => {
              if (!couponType) {
                toast.error('Debes seleccionar el tipo de cupón.');
                return;
              }
              if (!!amountError) {
                toast.error('Corrige el error en el monto del descuento.');
                return;
              }
              handleCreateCoupon();
            }}
          >
            Crear cupón
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateCoupon;


